/* eslint-disable */
import axios from 'axios';
import API_ROUTE from './serviceConstants';
import { encryptAccessToken, decryptAccessToken } from './Encrypt';
import { isTokenExpired } from '../middlewares/AuthCheck';
import serviceEndpoints from './serviceEndpoints';
import { Notifications, errorNotify } from '../utils';

const proposalGateway = axios.create({
    baseURL: API_ROUTE,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 20000,
});

// Dedupes concurrent token refreshes. Without this, two parallel 401 responses
// both POST /refresh with the same refresh token; if the server rotates refresh
// tokens, the second exchange fails and the user is silently logged out.
let refreshPromise = null;

const refreshAccessToken = (refreshToken) => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
        try {
            const axiosService = axios.create({
                baseURL: API_ROUTE,
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                },
                timeout: 20000,
            });
            const { data } = await axiosService.post(serviceEndpoints.refresh);
            if (!data?.accessToken) {
                throw new Error('Refresh response missing accessToken');
            }
            const encryptedAccess = encryptAccessToken(data.accessToken);
            localStorage.setItem('accessToken', encryptedAccess);
            if (data.refreshToken) {
                localStorage.setItem('refreshToken', encryptAccessToken(data.refreshToken));
            }
            return decryptAccessToken(encryptedAccess);
        } finally {
            refreshPromise = null;
        }
    })();
    return refreshPromise;
};

proposalGateway.interceptors.response.use(
    (response) => response,
    async ({ response, config }) => {
        if (response?.data?.statusCode === 400) {
            if (!errorNotify.includes(response.data?.errorName)) {
                const msg = response?.data?.message;
                const isHeygenQuotaError =
                    typeof msg === 'string' &&
                    (msg.toLowerCase().includes('status code 400') || msg.toLowerCase().includes('request failed'));
                if (!isHeygenQuotaError) {
                    if (Array.isArray(msg)) {
                        msg.forEach((item) => Notifications(item, 'error'));
                    } else {
                        Notifications(msg, 'error');
                    }
                }
            }
            return response;
        }
        if (response?.data?.statusCode === 401) {
            const encryptedRefreshToken = localStorage.getItem('refreshToken');
            let refreshToken = null;
            try {
                refreshToken = encryptedRefreshToken ? decryptAccessToken(encryptedRefreshToken) : null;
            } catch {
                refreshToken = null;
            }
            const isRefreshTokenExpired = !refreshToken || isTokenExpired(refreshToken);
            if (!isRefreshTokenExpired) {
                const accessToken = localStorage.getItem('accessToken');
                const decryptedToken = decryptAccessToken(accessToken);
                if (refreshToken && isTokenExpired(decryptedToken)) {
                    const newAccessToken = await refreshAccessToken(refreshToken);
                    config.headers.Authorization = `Bearer ${newAccessToken}`;
                    return proposalGateway(config);
                }
            } else {
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/';
            }
        }
        return response;
    }
);

proposalGateway.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');

    if (token) {
        const decryptedToken = decryptAccessToken(token);
        config.headers = { ...config.headers, Authorization: `Bearer ${decryptedToken}` };
    }

    return config;
});

export default proposalGateway;
